import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import {
  usePreviewEffect,
  usePreviewState,
  usePrevious,
  useResetComponent,
} from '@wix/editor-elements-preview-utils';
import type {
  IComponentPreviewWrapper,
  PreviewWrapperProps,
} from '@wix/editor-elements-types/thunderboltPreview';
import React, { useRef } from 'react';
import {
  IRadioGroupImperativeActions,
  IRadioGroupProps,
  RadioGroupPreviewWrapperProps,
} from '../RadioGroup.types';
import styles from './RadioButton/style/RadioButtonDefaultSkin.scss';

const noop = () => {};

function withComponentPreview(
  WrappedComponent: React.ComponentType<IRadioGroupProps>,
): IComponentPreviewWrapper<IRadioGroupProps> {
  return React.forwardRef<IRadioGroupImperativeActions, IRadioGroupProps>(
    (
      {
        previewWrapperProps = {},
        setValidityIndication = noop,
        setValue = noop,
        validateValue = noop,
        ...viewerProps
      }: PreviewWrapperProps<IRadioGroupProps, RadioGroupPreviewWrapperProps>,
      ref,
    ) => {
      const { shouldResetComponent, compPreviewState, componentViewMode } =
        previewWrapperProps;
      const { value } = viewerProps;

      const previousCompViewMode = usePrevious(componentViewMode);
      const defaultValue = useRef(viewerProps.value);

      const isInEditor = React.useCallback(
        () => componentViewMode === 'editor',
        [componentViewMode],
      );
      React.useEffect(() => {
        if (previousCompViewMode === 'editor' && isInEditor()) {
          defaultValue.current = value;
        }
      }, [previousCompViewMode, value, isInEditor]);

      usePreviewEffect({
        componentViewMode: componentViewMode || '',
        onPreviewViewMode: validateValue,
      });

      const key = useResetComponent({
        shouldResetComponent,
        id: viewerProps.id,
        onResetComponent: () => {
          setValue(defaultValue.current);
          setValidityIndication(false);
          validateValue();
        },
      });

      usePreviewState(viewerProps.id, compPreviewState, {
        error: {
          selectors: `.${styles.input}`,
          type: 'plural',
          state: 'invalid',
        },
        hover: {
          selectors: `.${styles.RadioButtonDefaultSkin}`,
          type: 'plural',
        },
        disabled: {
          selectors: `.${styles.RadioButtonDefaultSkin}`,
          type: 'plural',
        },
        focus: {
          selectors: `.${styles.RadioButtonDefaultSkin}`,
          type: 'plural',
        },
      });

      const forceValidityIndiction =
        componentViewMode === 'editor' && compPreviewState === 'error';
      const shouldShowValidityIndication =
        forceValidityIndiction || viewerProps.shouldShowValidityIndication;

      return (
        <WrappedComponent
          {...viewerProps}
          setValidityIndication={isInEditor() ? noop : setValidityIndication}
          key={key}
          ref={ref}
          shouldShowValidityIndication={shouldShowValidityIndication}
          setValue={setValue}
        />
      );
    },
  );
}

export default (ViewerComponent: React.ComponentType<IRadioGroupProps>) =>
  createComponentPreviewEntry(withComponentPreview(ViewerComponent));
